import Swiper from '../libs/swiper.min';
function setupAutoSlider() {
  // Slides may be a number or the keyword 'auto' (slide width = image width).
  var parseSlides = function parseSlides(value, fallback) {
    if (value === 'auto') return 'auto';
    var n = parseInt(value, 10);
    return Number.isNaN(n) ? fallback : n;
  };

  // The slidesPerView that applies to the current viewport width. Mirrors the
  // breakpoints handed to Swiper below so we can size the slide set correctly.
  var slidesForWidth = function slidesForWidth(slider) {
    var w = window.innerWidth;
    if (w >= 1200) return parseSlides(slider.getAttribute('data-slides-desktop'), 5);
    if (w >= 768) return parseSlides(slider.getAttribute('data-slides-tablet'), 4);
    if (w >= 320) return parseSlides(slider.getAttribute('data-slides-mobile'), 3);
    return parseSlides(slider.getAttribute('data-slides-small-mobile'), 2);
  };

  // Resolve once every image inside an element has a measurable width — needed
  // before we can measure 'auto' slide widths. Images are rendered with
  // loading="lazy", so an off-screen slider's images would otherwise never fire
  // 'load' and the slider would never initialize: force them eager, and never
  // block longer than `timeout` so setup always proceeds.
  var whenImagesReady = function whenImagesReady(el) {
    var timeout = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1500;
    var pending = Array.from(el.querySelectorAll('img')).filter(function (img) {
      return !img.complete || img.naturalWidth === 0;
    });
    if (!pending.length) return Promise.resolve();
    pending.forEach(function (img) {
      if (img.loading === 'lazy') img.loading = 'eager';
    });
    var loaded = Promise.all(pending.map(function (img) {
      return new Promise(function (resolve) {
        img.addEventListener('load', resolve, {
          once: true
        });
        img.addEventListener('error', resolve, {
          once: true
        });
      });
    }));
    var timer = new Promise(function (resolve) {
      return setTimeout(resolve, timeout);
    });
    return Promise.race([loaded, timer]);
  };
  var setupSlider = function setupSlider(slider) {
    var desktopHeight = slider.getAttribute('data-height-desktop') || '130px';
    var tabletHeight = slider.getAttribute('data-height-tablet') || '115px';
    var mobileHeight = slider.getAttribute('data-height-mobile') || '100px';
    var smallMobileHeight = slider.getAttribute('data-height-small-mobile') || '90px';
    var slidesDesktop = parseSlides(slider.getAttribute('data-slides-desktop'), 5);
    var slidesTablet = parseSlides(slider.getAttribute('data-slides-tablet'), 4);
    var slidesMobile = parseSlides(slider.getAttribute('data-slides-mobile'), 3);
    var slidesSmallMobile = parseSlides(slider.getAttribute('data-slides-small-mobile'), 2);
    var spaceBetweenAttr = parseInt(slider.getAttribute('data-space-between'), 10);
    var spaceBetween = Number.isNaN(spaceBetweenAttr) ? 44 : spaceBetweenAttr;
    var height = desktopHeight; // Default to desktop height
    if (window.innerWidth <= 576) {
      height = smallMobileHeight; // Small mobile height for widths <= 576px
    } else if (window.innerWidth <= 768) {
      height = mobileHeight; // Mobile height for widths <= 768px
    } else if (window.innerWidth <= 1024) {
      height = tabletHeight; // Tablet height for widths <= 1024px
    }
    slider.style.height = height;
    var wrapper = slider.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    // Tear down a previous instance (this also strips Swiper's own loop clones)
    // and restore the wrapper to the original, author-provided slides only, so
    // re-running on resize never compounds our duplicates.
    if (slider.swiperInstance) {
      slider.swiperInstance.destroy(true, true);
      slider.swiperInstance = null;
    }
    if (typeof slider._originalSlidesHTML === 'string') {
      wrapper.innerHTML = slider._originalSlidesHTML;
    } else {
      slider._originalSlidesHTML = wrapper.innerHTML;
    }
    var originals = Array.from(wrapper.querySelectorAll('.swiper-slide'));
    var originalCount = originals.length;
    if (!originalCount) return;
    var spv = slidesForWidth(slider);
    var viewport = (slider.querySelector('.swiper') || slider).clientWidth || window.innerWidth;

    // A seamless loop needs the base slide set to overflow the viewport with
    // room to spare; otherwise Swiper 5 disables looping and the track just
    // scrolls to the end and stops. Duplicate the original slides until they
    // comfortably exceed two viewports' worth of width.
    var repeats = 1;
    if (spv === 'auto') {
      var total = 0;
      originals.forEach(function (s) {
        total += s.getBoundingClientRect().width + spaceBetween;
      });
      if (total > 0) {
        repeats = Math.max(2, Math.ceil(viewport * 2 / total));
      }
    } else {
      // Numeric: guarantee at least ~2x slidesPerView real slides.
      var needed = Math.ceil(spv) * 2;
      repeats = Math.max(1, Math.ceil(needed / originalCount));
    }
    for (var r = 1; r < repeats; r++) {
      originals.forEach(function (slide) {
        var clone = slide.cloneNode(true);
        clone.setAttribute('data-marquee-clone', '');
        wrapper.appendChild(clone);
      });
    }

    // Real slides now present (originals + our fill clones). Swiper will clone
    // this whole set again on each side to build the loop.
    var slideCount = wrapper.querySelectorAll('.swiper-slide').length;
    slider.swiperInstance = new Swiper(slider.querySelector('.swiper'), {
      direction: 'horizontal',
      loop: true,
      loopedSlides: slideCount,
      loopAdditionalSlides: originalCount,
      autoplay: {
        delay: 0,
        disableOnInteraction: false
      },
      speed: 3000,
      centeredSlides: false,
      autoHeight: false,
      updateOnWindowResize: true,
      slidesPerView: slidesSmallMobile,
      spaceBetween: spaceBetween,
      breakpoints: {
        320: {
          slidesPerView: slidesMobile
        },
        768: {
          slidesPerView: slidesTablet
        },
        1200: {
          slidesPerView: slidesDesktop
        }
      }
    });
  };
  var updateSliderSettings = function updateSliderSettings() {
    document.querySelectorAll('.mad-auto-slider').forEach(function (slider) {
      // Wait for images so 'auto' slide widths measure correctly.
      whenImagesReady(slider).then(function () {
        return setupSlider(slider);
      });
    });
  };
  updateSliderSettings();

  // Re-measure once everything has painted, in case a slider set up via the
  // image-load timeout before its images had real dimensions.
  window.addEventListener('load', updateSliderSettings);

  // Debounce resize so we don't thrash destroy/clone/init.
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(updateSliderSettings, 200);
  });
  jQuery('.mad-auto-slider').addClass('initialized');
}
function initAutoSlider() {
  try {
    jQuery(setupAutoSlider);
  } catch (error) {
    console.error('Error initializing auto slider:', error);
  }
}
export default initAutoSlider;